import { ARIA, CLASS_NAMES, EVENTS, SELECTORS, MISC } from '@bs/constants';

/**
 * Tabs controller for Table of Contents navigation
 */
export default class TableOfContentsTabs {
  constructor(element = document) {
    this.element = element;
    this.init();
  }

  /**
   * Initialize controller.
   * @returns {this} Instance for chaining.
   */
  init() {
    this.cacheDomReferences().buildTabOrder().buildProgressSteps().enable();

    return this;
  }

  /**
   * Cache DOM references.
   * @returns {this} Instance for chaining.
   */
  cacheDomReferences() {
    // Tab triggers (dropdown + parents + children)
    this.tabTriggers = document.querySelectorAll(SELECTORS.DATA_TAB_ID);

    // Content panels
    this.contents = this.element.querySelectorAll(`.${CLASS_NAMES.TAB_PANEL}`);

    // Navigation
    this.prevBtn = this.element.querySelector(SELECTORS.DATA_TAB_PREV);
    this.nextBtn = this.element.querySelector(SELECTORS.DATA_TAB_NEXT);

    // Progress bar
    this.progressBar = this.element.querySelector(SELECTORS.DATA_TAB_PROGRESS);

    this.currentIndex = 0;
    this.tabOrder = [];

    return this;
  }

  /**
   * Build tab order based on trigger appearance in DOM.
   * @returns {this} Instance for chaining.
   */
  buildTabOrder() {
    this.tabTriggers.forEach((trigger) => {
      const id = trigger.dataset.tabId;
      if (id && !this.tabOrder.includes(id)) {
        this.tabOrder.push(id);
      }
    });

    return this;
  }

  /**
   * Enable tab interactions.
   * @returns {this} Instance for chaining.
   */
  enable() {
    if (!this.tabOrder.length) return this;

    // Trigger clicks
    this.tabTriggers.forEach((trigger) => {
      trigger.addEventListener(EVENTS.CLICK, () => {
        const id = trigger.dataset.tabId;
        if (!id) return;

        window.location.hash = id;
      });
    });

    // Prev / Next
    if (this.prevBtn) {
      this.prevBtn.addEventListener(EVENTS.CLICK, () => {
        this.goPrev();
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener(EVENTS.CLICK, () => {
        this.goNext();
      });
    }

    window.addEventListener('hashchange', () => {
      this.activateById(window.location.hash.slice(1));
    });

    // Initial state
    const hashId = window.location.hash.slice(1);
    if (this.tabOrder.includes(hashId)) {
      this.activateById(hashId);
    } else {
      this.activateByIndex(0, false);
    }

    return this;
  }

  /**
   * Activate tab by ID.
   * @param {string} id - Target tab id to activate.
   * @returns {void}
   */
  activateById(id) {
    const index = this.tabOrder.indexOf(id);
    if (index === -1) return;

    this.activateByIndex(index);
  }

  /**
   * Scroll tabs container to the top of the viewport.
   * Ensures consistent scroll position when switching tabs
   * with dynamic-height content.
   * @returns {void}
   */
  scrollToTop() {
    if (!this.element) return;

    this.element.scrollIntoView({
      behavior: MISC.SMOOTH,
      block: MISC.START
    });
  }

  /**
   * Core activation logic.
   * @param {number} index - Tab index to activate.
   * @param {boolean} shouldScroll - Whether to scroll the container to top.
   * @returns {void}
   */
  activateByIndex(index, shouldScroll = true) {
    const activeId = this.tabOrder[index];
    const target = document.getElementById(activeId);

    this.currentIndex = index;
    this.tabTriggers.forEach((trigger) => {
      trigger.classList.remove(CLASS_NAMES.OPEN);
    });

    this.contents.forEach((content) => {
      content.classList.remove(CLASS_NAMES.OPEN);
    });

    // Close any open dropdowns inside this component before opening the
    // dropdown that contains the active trigger (if any).
    const dropdowns = this.element.querySelectorAll(SELECTORS.DROPDOWN);
    dropdowns.forEach((d) => {
      const toggle = d.querySelector(SELECTORS.DROPDOWN_TOGGLE);
      const panel = d.querySelector(SELECTORS.DROPDOWN_CONTENT);
      if (toggle) toggle.classList.remove(CLASS_NAMES.OPEN);
      d.classList.remove(CLASS_NAMES.OPEN);
      if (panel) {
        panel.classList.remove(CLASS_NAMES.OPENED, CLASS_NAMES.FADE);
        panel.setAttribute(ARIA.HIDDEN, 'true');
      }
    });

    this.tabTriggers.forEach((trigger) => {
      if (trigger.dataset.tabId === activeId) {
        trigger.classList.add(CLASS_NAMES.OPEN);

        // If the trigger is inside a dropdown (parent has children), open
        // that dropdown so the trigger is visible.
        const parentDropdown = trigger.closest(SELECTORS.DROPDOWN);
        if (parentDropdown) {
          const parentToggle = parentDropdown.querySelector(
            SELECTORS.DROPDOWN_TOGGLE
          );
          const parentPanel = parentDropdown.querySelector(
            SELECTORS.DROPDOWN_CONTENT
          );

          if (parentToggle) parentToggle.classList.add(CLASS_NAMES.OPEN);
          parentDropdown.classList.add(CLASS_NAMES.OPEN);
          if (parentPanel) {
            parentPanel.classList.add(CLASS_NAMES.OPENED);
            parentPanel.setAttribute(ARIA.HIDDEN, 'false');
          }
        }
      }
    });

    if (target) target.classList.add(CLASS_NAMES.OPEN);

    this.updateProgress();
    this.updateNavState();
    if (shouldScroll) {
      this.scrollToTop();
    }
  }

  /**
   * Previous tab
   */
  goPrev() {
    if (this.currentIndex > 0) {
      window.location.hash = this.tabOrder[this.currentIndex - 1];
    }
  }

  /**
   * Next tab
   */
  goNext() {
    if (this.currentIndex < this.tabOrder.length - 1) {
      window.location.hash = this.tabOrder[this.currentIndex + 1];
    }
  }

  /**
   * Update progress bar
   */
  updateProgress() {
    if (!this.progressSteps) return;

    this.progressSteps.forEach((step, index) => {
      step.classList.remove(CLASS_NAMES.IS_ACTIVE, CLASS_NAMES.IS_COMPLETE);
      if (index < this.currentIndex) {
        step.classList.add(CLASS_NAMES.IS_COMPLETE);
      }

      if (index === this.currentIndex) {
        step.classList.add(CLASS_NAMES.IS_ACTIVE);
      }
    });
  }

  /**
   * Enable / disable prev-next buttons
   */
  updateNavState() {
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentIndex === 0;
    }

    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentIndex === this.tabOrder.length - 1;
    }
  }

  /**
   * Build progress steps (for step indicators).
   * @returns {this} Instance for chaining.
   */
  buildProgressSteps() {
    if (!this.progressBar) return this;

    this.progressBar.innerHTML = '';
    this.tabOrder.forEach((id) => {
      const step = document.createElement(SELECTORS.DIV);

      step.classList.add(CLASS_NAMES.TABS_PROGRESS_STEP);
      step.dataset.tabId = id;
      this.progressBar.appendChild(step);
    });

    this.progressSteps = this.progressBar.querySelectorAll(
      `.${CLASS_NAMES.TABS_PROGRESS_STEP}`
    );

    return this;
  }
}
